import React, { useEffect, useState } from "react";
import { Modal, Form, Input, message, Select, Row, Col, Switch, Upload, Button, Spin } from "antd";
import { UploadOutlined, UserOutlined, PlusOutlined, EditOutlined, EnvironmentOutlined } from "@ant-design/icons";
import UserServ from "../service/UserServ";
import GlobalButton from "../util/GlobalButton";
import DeliveryAddressModel from "./DeliveryAddressModel";
import { getImageUrl } from "./SafeImage";

const { Option } = Select;

const AddUser = ({ open, userDetails, isEditing, refreshUsers }) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [imagePreview, setImagePreview] = useState("");
    const [addressModalOpen, setAddressModalOpen] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [addressList, setAddressList] = useState([]);

    const rolesList = [
        { value: "user", label: "User (Buyer)" },
        { value: "seller", label: "Seller" },
        { value: "admin", label: "Admin" },
        { value: "superadmin", label: "Super Admin" },
    ];

    useEffect(() => {
        if (isEditing && userDetails) {
            form.setFieldsValue({
                firstName: userDetails.firstName,
                lastName: userDetails.lastName,
                email: userDetails.email,
                phone: userDetails.phone,
                role: userDetails.role || "user",
                isActive: userDetails.isActive !== false,
                isBlocked: !!userDetails.isBlocked,
                profilePhoto: userDetails.profilePhoto || userDetails.profileImage || userDetails.imageUrl || ""
            });
            setImagePreview(getImageUrl(userDetails.profilePhoto || userDetails.profileImage || userDetails.imageUrl) || "");
            
            if (Array.isArray(userDetails.address)) {
                setAddressList(userDetails.address);
            } else if (userDetails.address) {
                // If it's a single object, put it in an array
                setAddressList([userDetails.address]);
            } else {
                setAddressList([]);
            }
        } else {
            form.resetFields();
            form.setFieldsValue({ isActive: true, isBlocked: false });
            setImagePreview("");
            setAddressList([]);
        }
    }, [isEditing, userDetails, form]);

    const handleAddressRefresh = async (isRefresh) => {
        setAddressModalOpen(false);
        if (isRefresh && userDetails?._id) {
            try {
                const res = await UserServ.single(userDetails._id);
                if (res?.data?.address) {
                    setAddressList(Array.isArray(res.data.address) ? res.data.address : [res.data.address]);
                }
            } catch (err) {
                console.error("Failed to fetch updated addresses", err);
            }
        }
    };

    const [isUploadingImage, setIsUploadingImage] = useState(false);

    const handleImageUpload = async (file) => {
        try {
            setIsUploadingImage(true);
            const formData = new FormData();
            formData.append("image", file);
            const res = await UserServ.uploadImage(formData);
            if (res && res.imageUrl) {
                setImagePreview(res.imageUrl);
                form.setFieldsValue({ profilePhoto: res.imageUrl });
                message.success("Image uploaded successfully");
            }
        } catch (error) {
            message.error("Failed to upload image");
        } finally {
            setIsUploadingImage(false);
        }
        return false;
    };

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            const values = await form.validateFields();

            const payload = {
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                phone: values.phone,
                password: values.password,
                role: values.role,
                isActive: values.isActive,
                isBlocked: values.isBlocked,
                profilePhoto: values.profilePhoto
            };

            if (isEditing && userDetails?._id) {
                await UserServ.update(userDetails._id, payload);
                message.success("User updated successfully");
            } else {
                await UserServ.create(payload);
                message.success("User added successfully");
            }

            refreshUsers(true);
        } catch (error) {
            console.error("Error saving user:", error);
            if (!error.errorFields) {
                message.error(error.message || "Failed to save user");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const labelStyle = { fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: 4 };

    return (
        <Modal
            open={open}
            onCancel={() => refreshUsers(false)}
            footer={null}
            width={550}
            centered
            closable={false}
            className="ss-modal"
            bodyStyle={{ padding: 0 }}
            maskStyle={{ backdropFilter: 'blur(3px)' }}
            style={{ borderRadius: 12, overflow: 'hidden' }}
            styles={{ content: { padding: 0, borderRadius: 12, overflow: 'hidden' }, body: { padding: 0 } }}
        >
            <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden' }}>
                {/* Header Section */}
                <div style={{ 
                    padding: '12px 16px', 
                    background: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)', 
                    borderBottom: '1px solid #e2e8f0', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#1e293b' }}>
                            {isEditing ? "Edit User Details" : "Create New User"}
                        </h2>
                        <p style={{ margin: '2px 0 0', fontSize: 10, color: '#64748b', fontWeight: 600 }}>
                            {isEditing ? "Update account information" : "Fill in details to register user"}
                        </p>
                    </div>
                    <button 
                        onClick={() => refreshUsers(false)}
                        style={{ background: '#fff', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 12, width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
                    >
                        <span style={{ fontSize: 12 }}>×</span>
                    </button>
                </div>

                {/* Form Content */}
                <div style={{ padding: '16px 20px', maxHeight: '70vh', overflowY: 'auto' }}>
                    <Form layout="vertical" form={form} requiredMark={false} size="small">
                        <Row gutter={16}>
                            {/* Left Column: Profile Image */}
                            <Col xs={24} md={8} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: 12 }}>
                                    {imagePreview ? (
                                        <img src={imagePreview} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <UserOutlined style={{ fontSize: 24, color: '#94a3b8' }} />
                                    )}
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <Form.Item name="profilePhoto" style={{ marginBottom: 0 }}>
                                        <Upload beforeUpload={handleImageUpload} showUploadList={false} accept="image/*">
                                            <Button icon={isUploadingImage ? <Spin size="small" /> : <UploadOutlined />} size="small" style={{ borderRadius: 4, fontSize: 10, fontWeight: 600 }}>
                                                {isUploadingImage ? "Uploading..." : "Upload Photo"}
                                            </Button>
                                        </Upload>
                                    </Form.Item>
                                    <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 4 }}>Recommended: 200x200px</div>
                                </div>
                            </Col>

                            {/* Right Column: Name Fields */}
                            <Col xs={24} md={16}>
                                <Row gutter={12}>
                                    <Col xs={24}>
                                        <Form.Item
                                            name="firstName"
                                            label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>First Name <span style={{color: '#ef4444'}}>*</span></span>}
                                            rules={[{ required: !isEditing, message: "Required" }]}
                                            style={{ marginBottom: 8 }}
                                        >
                                            <Input placeholder="First name" style={{ borderRadius: 4 }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                        <Form.Item
                                            name="lastName"
                                            label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>Last Name <span style={{color: '#ef4444'}}>*</span></span>}
                                            rules={[{ required: !isEditing, message: "Required" }]}
                                            style={{ marginBottom: 8 }}
                                        >
                                            <Input placeholder="Last name" style={{ borderRadius: 4 }} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="email"
                                    label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>Email Address <span style={{color: '#ef4444'}}>*</span></span>}
                                    rules={[
                                        { required: !isEditing, message: "Required" },
                                        { type: "email", message: "Valid email required" }
                                    ]}
                                    style={{ marginBottom: 8 }}
                                >
                                    <Input placeholder="user@example.com" style={{ borderRadius: 4 }} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="phone"
                                    label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>Phone Number <span style={{color: '#ef4444'}}>*</span></span>}
                                    rules={[{ required: !isEditing, message: "Required" }]}
                                    style={{ marginBottom: 8 }}
                                >
                                    <Input placeholder="+1 (555) 000-0000" style={{ borderRadius: 4 }} />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} md={!isEditing ? 12 : 24}>
                                <Form.Item
                                    name="role"
                                    label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>Account Role <span style={{color: '#ef4444'}}>*</span></span>}
                                    rules={[{ required: !isEditing, message: "Required" }]}
                                    style={{ marginBottom: 8 }}
                                >
                                    <Select placeholder="Select role" style={{ borderRadius: 4 }}>
                                        {rolesList.map((role) => (
                                            <Option key={role.value} value={role.value}>
                                                {role.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            {!isEditing && (
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="password"
                                        label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>Password <span style={{color: '#ef4444'}}>*</span></span>}
                                        rules={[
                                            { required: true, message: "Required" },
                                            { min: 6, message: "Min 6 characters" }
                                        ]}
                                        style={{ marginBottom: 8 }}
                                    >
                                        <Input.Password placeholder="Password" style={{ borderRadius: 6 }} />
                                    </Form.Item>
                                </Col>
                            )}

                            <Col xs={24}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                    <span style={{...labelStyle, fontSize: 9, marginBottom: 0}}>Saved Addresses</span>
                                    {isEditing && (
                                        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => { setSelectedAddress(null); setAddressModalOpen(true); }} style={{ borderRadius: 4, fontSize: 10, padding: '0 8px', height: 24, color: '#6366f1', borderColor: '#c7d2fe', background: '#eef2ff' }}>
                                            Add Address
                                        </Button>
                                    )}
                                </div>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                                    {addressList.length > 0 ? addressList.map((addr, idx) => (
                                        <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: 8, position: 'relative', background: '#f8fafc' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                                                <EnvironmentOutlined style={{ color: '#6366f1', fontSize: 10 }} />
                                                <span style={{ fontSize: 9, fontWeight: 700, color: '#1e293b', textTransform: 'uppercase' }}>{addr.addressType || 'Address'}</span>
                                            </div>
                                            <p style={{ margin: 0, fontSize: 10, color: '#64748b', lineHeight: 1.3 }}>
                                                {[addr.street || addr.addressLine, addr.city, addr.district, addr.state, addr.pincode || addr.zipCode].filter(Boolean).join(", ")}
                                            </p>
                                            {isEditing && (
                                                <button 
                                                    type="button"
                                                    onClick={() => { setSelectedAddress(addr); setAddressModalOpen(true); }}
                                                    style={{ position: 'absolute', bottom: 8, right: 8, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', color: '#6366f1', padding: 4, display: 'flex', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                                                >
                                                    <EditOutlined style={{ fontSize: 10 }} />
                                                </button>
                                            )}
                                        </div>
                                    )) : (
                                        <div style={{ gridColumn: '1 / -1', padding: '12px', textAlign: 'center', background: '#f8fafc', borderRadius: 6, border: '1px dashed #cbd5e1', fontSize: 10, color: '#94a3b8' }}>
                                            {isEditing ? "No addresses saved. Click 'Add Address' to create one." : "Save the user first to add addresses."}
                                        </div>
                                    )}
                                </div>
                            </Col>

                            {isEditing && (
                                <>
                                    <Col xs={12}>
                                        <Form.Item shouldUpdate={(prev, curr) => prev.isActive !== curr.isActive} noStyle>
                                            {({ getFieldValue }) => (
                                                <Form.Item
                                                    name="isActive"
                                                    label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>Active Status</span>}
                                                    valuePropName="checked"
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Switch size="small" style={{ background: getFieldValue('isActive') ? '#10b981' : '#cbd5e1' }} />
                                                </Form.Item>
                                            )}
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Item shouldUpdate={(prev, curr) => prev.isBlocked !== curr.isBlocked} noStyle>
                                            {({ getFieldValue }) => (
                                                <Form.Item
                                                    name="isBlocked"
                                                    label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>Block Status</span>}
                                                    valuePropName="checked"
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Switch size="small" style={{ background: getFieldValue('isBlocked') ? '#ef4444' : '#cbd5e1' }} />
                                                </Form.Item>
                                            )}
                                        </Form.Item>
                                    </Col>
                                </>
                            )}
                        </Row>
                    </Form>
                </div>

                {/* Footer Section */}
                <div style={{ padding: '12px 16px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                    <button 
                        onClick={() => refreshUsers(false)}
                        disabled={submitting}
                        style={{
                            padding: '6px 16px', borderRadius: 6, border: '1px solid #e2e8f0',
                            background: '#fff', color: '#64748b', fontSize: 11, fontWeight: 700,
                            cursor: submitting ? 'not-allowed' : 'pointer', transition: 'all 0.2s'
                        }}
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleSubmit}
                        disabled={submitting}
                        style={{
                            padding: '6px 20px', borderRadius: 6, border: 'none',
                            background: submitting ? '#9ca3af' : '#4F46E5', color: '#fff', fontSize: 11, fontWeight: 700,
                            cursor: submitting ? 'not-allowed' : 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(79, 70, 229, 0.2)'
                        }}
                    >
                        {submitting ? 'Saving...' : (isEditing ? 'Update User' : 'Save User')}
                    </button>
                </div>
            </div>

            {/* Nested Address Modal */}
            {addressModalOpen && (
                <DeliveryAddressModel
                    open={addressModalOpen}
                    onClose={() => setAddressModalOpen(false)}
                    refreshAddress={handleAddressRefresh}
                    addressDetails={selectedAddress}
                    isEditing={!!selectedAddress}
                    overrideUser={userDetails ? {
                        id: userDetails._id,
                        name: userDetails.firstName + ' ' + userDetails.lastName,
                        email: userDetails.email,
                        phone: userDetails.phone
                    } : null}
                />
            )}
        </Modal>
    );
};

export default AddUser;
